import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);
import {
  filterDiagnostics,
  newTenderShare,
  rangeError,
  recentRange,
  type AnalysisRange,
} from "./analysis";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Select,
  Segmented,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from "antd";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FieldQualitySection } from "./fieldQuality";
import {
  HEALTH_COLORS,
  HEALTH_LABELS,
  RUN_STATUS_COLORS,
  RUN_STATUS_LABELS,
  type Health,
  type ReconciliationRow,
  type RunStatus,
  type ScraperCatalogRow,
  type ScraperDiagnosticsResponse,
  type ScraperSourceHealth,
} from "../../types/scraperDiagnostics";

const { Title, Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

// Lokalne kopie helperów auth — taka jest konwencja w tym panelu (patrz `pages/funnel/list.tsx`);
// nie ma wspólnego modułu i celowo go tu nie wprowadzamy.
function adminHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
}

async function adminFetch<T>(url: string): Promise<T> {
  const response = await fetch(url, { headers: adminHeaders() });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || `Request failed: ${response.statusText}`);
  }
  return response.json() as Promise<T>;
}

/** Paleta per źródło — stała kolejność, żeby kolor źródła nie skakał między odświeżeniami. */
const SOURCE_COLORS = [
  "#1F8081",
  "#CF1322",
  "#D48806",
  "#2F54EB",
  "#722ED1",
  "#08979C",
  "#C41D7F",
  "#5B8C00",
  "#AD6800",
  "#096DD9",
  "#9E1068",
  "#389E0D",
];

function sourceColor(sources: string[], source: string): string {
  const index = sources.indexOf(source);
  return SOURCE_COLORS[(index < 0 ? 0 : index) % SOURCE_COLORS.length];
}

function fmtTime(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("pl-PL", {
    dateStyle: "short",
    timeStyle: "medium",
    timeZone: "Europe/Warsaw",
  });
}

function activityState(activeCount: number | null | undefined): string {
  if (activeCount === null || activeCount === undefined) {
    return "stan aktywności: nieznany (starsza telemetria)";
  }
  if (activeCount > 0) return `operacja nadal aktywna (${activeCount})`;
  return "operacja zakończona";
}

function hasActivityHint(row: ScraperSourceHealth): boolean {
  // `active_count: 0` jest emitowane też przed pierwszą aktywnością, więc nie jest dowodem
  // zakończenia. Wyświetlamy tylko faktycznie zidentyfikowaną operację z czasem startu.
  return Boolean(row.last_activity_kind && row.last_activity_started_at);
}

function fmtAge(minutes: number | null): string {
  if (minutes === null || minutes === undefined) return "—";
  if (minutes < 60) return `${minutes} min przed końcem okresu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ${minutes % 60} min przed końcem okresu`;
  return `${Math.floor(hours / 24)} dni przed końcem okresu`;
}

function fmtDuration(ms: number | null): string {
  if (!ms) return "—";
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}

function HealthTag({ health }: { health: Health }) {
  return (
    <Tag color={HEALTH_COLORS[health]} style={{ fontWeight: 600 }}>
      {HEALTH_LABELS[health]}
    </Tag>
  );
}

function YesNo({ value }: { value: boolean | null }) {
  if (value === null) return <Text type="secondary">—</Text>;
  return <Tag color={value ? HEALTH_COLORS.ok : "default"}>{value ? "Tak" : "Nie"}</Tag>;
}

const STATUS_LABELS = {
  unknown: "Brak danych",
  working: "Działa",
  not_working: "Nie działa",
  attention: "Wymaga uwagi",
} as const;

/**
 * Timeline runów — pasek per źródło, pozycja i szerokość liczone z okna analizy.
 * Dziura w pasku = pod nie odpalił albo został ubity; to czego `kubectl logs` nie pokaże.
 */
function RunTimeline({
  data,
  colorBorder,
}: {
  data: ScraperDiagnosticsResponse;
  colorBorder: string;
}) {
  const since = new Date(data.window.since).getTime();
  const until = new Date(data.window.until).getTime();
  const span = Math.max(until - since, 1);

  const lanes = useMemo(() => {
    const grouped = new Map<string, ScraperDiagnosticsResponse["timeline"]["runs"]>();
    for (const run of data.timeline.runs) {
      const list = grouped.get(run.source) ?? [];
      list.push(run);
      grouped.set(run.source, list);
    }
    return [...grouped.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data.timeline.runs]);

  if (!data.timeline.available) {
    return <Alert type="warning" showIcon message="Nie udało się pobrać timeline'u runów." />;
  }
  if (lanes.length === 0) {
    return data.timeline.truncated
      ? <Alert type="info" showIcon message="Historia została ograniczona. Dla wybranego źródła może brakować uruchomień — zawęź zakres dat." />
      : <Empty description="Brak runów w wybranym oknie" />;
  }

  return (
    <>
      {data.timeline.truncated && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Pokazano ostatnie ${data.timeline.limit} runów — zawęź okno, żeby zobaczyć wszystkie.`}
        />
      )}
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 640 }}>
          {lanes.map(([source, runs]) => (
            <Row key={source} align="middle" style={{ marginBottom: 6 }} wrap={false}>
              <Col flex="180px">
                <Text style={{ fontSize: 12 }}>{source}</Text>
              </Col>
              <Col flex="auto">
                <div
                  style={{
                    position: "relative",
                    height: 18,
                    background: colorBorder,
                    borderRadius: 4,
                  }}
                >
                  {runs.map((run, index) => {
                    if (!run.started_at) return null;
                    const start = new Date(run.started_at).getTime();
                    const end = run.finished_at ? new Date(run.finished_at).getTime() : until;
                    const left = ((start - since) / span) * 100;
                    // Minimalna szerokość, żeby run trwający 2 s nie zniknął z widoku.
                    const width = Math.max(((end - start) / span) * 100, 0.5);
                    return (
                      <Tooltip
                        key={`${run.source}-${run.started_at}-${index}`}
                        title={
                          <div style={{ fontSize: 12 }}>
                            <div>
                              <b>{RUN_STATUS_LABELS[run.status] ?? run.status}</b>
                            </div>
                            <div>start: {fmtTime(run.started_at)}</div>
                            <div>czas: {fmtDuration(run.duration_ms)}</div>
                            <div>
                              nowe: {run.created} / rewizje: {run.revised} / odrzuty:{" "}
                              {run.upserts_failed}
                            </div>
                            {run.exit_code !== null && <div>exit: {run.exit_code}</div>}
                            {run.rss_mb_max > 0 && <div>RSS max: {run.rss_mb_max} MiB</div>}
                          </div>
                        }
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: `${Math.min(Math.max(left, 0), 99.5)}%`,
                            width: `${Math.min(width, 100)}%`,
                            top: 0,
                            bottom: 0,
                            background: RUN_STATUS_COLORS[run.status] ?? HEALTH_COLORS.unknown,
                            borderRadius: 3,
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </div>
              </Col>
            </Row>
          ))}
        </div>
      </div>
      <Space size={12} wrap style={{ marginTop: 12 }}>
        {(Object.keys(RUN_STATUS_LABELS) as RunStatus[]).map((status) => (
          <Space key={status} size={4}>
            <span
              style={{
                display: "inline-block",
                width: 12,
                height: 12,
                borderRadius: 2,
                background: RUN_STATUS_COLORS[status],
              }}
            />
            <Text style={{ fontSize: 12 }} type="secondary">
              {RUN_STATUS_LABELS[status]}
            </Text>
          </Space>
        ))}
      </Space>
    </>
  );
}

/** Trend yieldu (nowe + rewizje) z rollupów godzinowych — linia per źródło. */
function YieldTrend({ data }: { data: ScraperDiagnosticsResponse }) {
  const { points, available } = data.yield_trend;

  const { sources, rows } = useMemo(() => {
    const sourceSet = [...new Set(points.map((p) => p.source))].sort();
    const byBucket = new Map<string, Record<string, number | string>>();
    for (const point of points) {
      if (!point.bucket) continue;
      const row = byBucket.get(point.bucket) ?? { bucket: point.bucket };
      row[point.source] = point.created + point.revised;
      byBucket.set(point.bucket, row);
    }
    const rows = [...byBucket.values()].sort((a, b) =>
      String(a.bucket).localeCompare(String(b.bucket)),
    );
    return { sources: sourceSet, rows };
  }, [points]);

  if (!available) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Nie udało się pobrać rollupów godzinowych (`scraper_rollup_hourly`)."
      />
    );
  }
  if (rows.length === 0) {
    return <Empty description="Brak pełnych godzin danych w wybranym okresie" />;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis
          dataKey="bucket"
          tickFormatter={(value: string) =>
            new Date(value).toLocaleString("pl-PL", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
            })
          }
          tick={{ fontSize: 11 }}
          minTickGap={40}
        />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <ChartTooltip labelFormatter={(value) => fmtTime(String(value))} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {sources.map((source) => (
          <Line
            key={source}
            type="monotone"
            dataKey={source}
            stroke={sourceColor(sources, source)}
            strokeWidth={2}
            dot={false}
            connectNulls
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

/** Ruch do źródeł: kategorie rozłączne, żeby podzbiory `failed` nie były sumowane podwójnie. */
function TrafficHealth({ sources }: { sources: ScraperSourceHealth[] }) {
  const rows = useMemo(
    () =>
      sources
        .filter((s) => s.requests_total > 0)
        .map((s) => ({
          source: s.source,
          ok: s.requests_ok,
          failed: Math.max(
            s.requests_failed - s.requests_blocked - (s.requests_auth_required ?? 0),
            0,
          ),
          blocked: s.requests_blocked,
          authRequired: s.requests_auth_required ?? 0,
        })),
    [sources],
  );

  if (rows.length === 0) {
    return <Empty description="Brak zarejestrowanego ruchu w tym oknie" />;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="source" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={70} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <ChartTooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="ok" name="Udane" stackId="req" fill={HEALTH_COLORS.ok} />
        <Bar dataKey="failed" name="Pozostałe nieudane" stackId="req" fill={HEALTH_COLORS.warn} />
        <Bar
          dataKey="blocked"
          name="Zablokowane (401/403/429)"
          stackId="req"
          fill={HEALTH_COLORS.crit}
        />
        <Bar
          dataKey="authRequired"
          name="Oczekiwane 401 (wliczone w nieudane)"
          stackId="req"
          fill="#2F54EB"
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Szczyty pamięci per źródło vs limit poda — zapowiedź OOMKilled, zanim pod padnie. */
function MemoryUsage({ sources, limitMb }: { sources: ScraperSourceHealth[]; limitMb: number }) {
  const rows = sources.filter((s) => s.rss_mb_max > 0).sort((a, b) => b.rss_mb_max - a.rss_mb_max);
  if (rows.length === 0) {
    return <Empty description="Brak próbek pamięci (telemetria nie raportowała RSS)" />;
  }
  return (
    <Space direction="vertical" style={{ width: "100%" }} size={10}>
      {rows.map((row) => {
        const pct = Math.round((row.rss_mb_max / limitMb) * 100);
        return (
          <div key={row.source}>
            <Row justify="space-between">
              <Col>
                <Text style={{ fontSize: 12 }}>{row.source}</Text>
              </Col>
              <Col>
                <Text style={{ fontSize: 12 }} type="secondary">
                  {row.rss_mb_max} / {limitMb} MiB
                </Text>
              </Col>
            </Row>
            <Progress
              percent={Math.min(pct, 100)}
              size="small"
              strokeColor={pct >= 85 ? HEALTH_COLORS.crit : HEALTH_COLORS.ok}
              format={() => `${pct}%`}
            />
          </div>
        );
      })}
    </Space>
  );
}

export const ScraperDiagnosticsList = () => {
  const { notification } = App.useApp();
  const { token } = theme.useToken();

  const [range, setRange] = useState<Partial<AnalysisRange>>(() => recentRange(1));
  const [rawData, setRawData] = useState<ScraperDiagnosticsResponse | null>(null);
  const [applied, setApplied] = useState<AnalysisRange | null>(null);
  const [view, setView] = useState<string>("overview");
  const [source, setSource] = useState("");
  const [rollingDays, setRollingDays] = useState<number | null>(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [receivedAt, setReceivedAt] = useState<string | null>(null);
  const requestId = useRef(0);
  const data = useMemo(
    () => (rawData ? filterDiagnostics(rawData, source) : null),
    [rawData, source],
  );
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (active: AnalysisRange) => {
      const validation = rangeError(active);
      if (validation) {
        notification.error({ message: validation });
        return;
      }
      const id = ++requestId.current;
      setLoading(true);
      setLoadError(null);
      try {
        const query = new URLSearchParams();
        if (active.since) query.set("since", active.since);
        if (active.until) query.set("until", active.until);
        const qs = query.toString();
        const response = await adminFetch<ScraperDiagnosticsResponse>(
          `${API_URL}/api/v1/admin/scraper/diagnostics${qs ? `?${qs}` : ""}`,
        );
        if (id !== requestId.current) return;
        setRawData(response);
        setApplied({
          since: response.window.since,
          until: response.window.until,
        });
        setReceivedAt(new Date().toISOString());
      } catch (error) {
        if (id !== requestId.current) return;
        setLoadError(error instanceof Error ? error.message : "Błąd pobierania danych");
        notification.error({
          message: "Nie udało się pobrać diagnostyki scrapera",
          description: error instanceof Error ? error.message : "Wystąpił nieoczekiwany błąd.",
        });
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    },
    [notification],
  );

  useEffect(() => {
    const initial = recentRange(1);
    setRange(initial);
    void load(initial);
    const requests = requestId;
    return () => {
      requests.current++;
    };
  }, [load]);

  const allNew =
    rawData?.health.available && rawData.health.has_telemetry
      ? rawData.health.totals.created
      : null;
  const health = data?.health;
  const recon = data?.reconciliation;
  const catalog = useMemo(() => {
    const result = rawData?.catalog;
    if (!result || result.status_policy) return result;
    return {
      ...result,
      rows: result.rows.map((row) => ({
        ...row,
        status: "unknown" as const,
        health: "unknown" as const,
        status_reasons: ["Serwer nie udostępnia jeszcze oceny z doby roboczej."],
      })),
    };
  }, [rawData]);
  const catalogRows =
    catalog?.rows.filter(
      (row) =>
        `${row.source_name} ${row.source_key}`
          .toLocaleLowerCase("pl")
          .includes(search.toLocaleLowerCase("pl")) &&
        (!status || row.status === status),
    ) ?? [];
  const invalidRange = rangeError(range);
  const applyRecent = (days: number) => {
    const next = recentRange(days);
    setRollingDays(days);
    setRange(next);
    void load(next);
  };
  // Telemetria milczy: albo zapytanie padło, albo w oknie nie ma ANI JEDNEGO runu. W obu
  // przypadkach zielone wiersze nic nie znaczą — mówimy o tym wprost, zamiast pokazywać zera.
  const telemetrySilent = !!health && (!health.available || !health.has_telemetry);
  // Bez manifestu panel nie wie, jakich źródeł się spodziewać — więc „cisza" przestaje być
  // odróżnialna od „nie ma czego pokazywać". To musi być widoczne, nie przemilczane.
  const scheduleMissing = !!health && !health.schedule?.available;

  const healthColumns = useMemo(
    () => [
      {
        title: "Źródło",
        dataIndex: "source",
        width: 200,
        fixed: "left" as const,
        render: (source: string, row: ScraperSourceHealth) => (
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 13 }}>
              {source}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.source_name}
            </Text>
          </Space>
        ),
      },
      {
        title: "Zdrowie",
        dataIndex: "health",
        width: 130,
        render: (value: Health) => <HealthTag health={value} />,
      },
      {
        title: "Ostatni run",
        dataIndex: "last_started_at",
        width: 190,
        render: (value: string | null, row: ScraperSourceHealth) => (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>{fmtTime(value)}</Text>
            {row.last_status && (
              <Tooltip
                title={
                  row.last_status === "running"
                    ? "Run jest aktywny. Świeży heartbeat oznacza stan W toku; watchdog scrapera osobno przerywa run bez postępu po 15 min."
                    : undefined
                }
              >
                <Tag
                  color={RUN_STATUS_COLORS[row.last_status] ?? HEALTH_COLORS.unknown}
                  style={{ fontSize: 11 }}
                >
                  {RUN_STATUS_LABELS[row.last_status] ?? row.last_status}
                </Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: "Ostatni sukces",
        dataIndex: "last_success_age_min",
        width: 140,
        render: (value: number | null) => <Text style={{ fontSize: 12 }}>{fmtAge(value)}</Text>,
      },
      { title: "Runy", dataIndex: "runs", width: 80 },
      {
        title: "Nowe",
        dataIndex: "created",
        width: 90,
        render: (value: number) => <Text strong>{value}</Text>,
      },
      {
        title: "Udział wszystkich nowych",
        key: "new_share",
        width: 220,
        sorter: (a: ScraperSourceHealth, b: ScraperSourceHealth) => a.created - b.created,
        render: (_: unknown, row: ScraperSourceHealth) => {
          const share = newTenderShare(row.created, allNew);
          return share === null ? (
            <Text type="secondary">—</Text>
          ) : (
            <Tooltip title="Udział w nowych przetargach ze wszystkich źródeł w wybranym okresie. Filtr źródła nie zmienia podstawy procentu.">
              <Text>
                {row.created.toLocaleString("pl-PL")} / {allNew?.toLocaleString("pl-PL")} ·{" "}
                {share.toLocaleString("pl-PL", { maximumFractionDigits: 2 })}%
              </Text>
            </Tooltip>
          );
        },
      },
      { title: "Rewizje", dataIndex: "revised", width: 90 },
      { title: "Bez zmian", dataIndex: "unchanged", width: 100 },
      {
        // Skip-check M4: TED/PZ pobierają listing, pytają Mercury które linki już zna i
        // upsertują TYLKO nieznane. Ta kolumna wyjaśnia, czemu zdrowy cykl ma 0 nowych.
        title: "Znane / kandydaci",
        dataIndex: "lookup_total",
        width: 150,
        render: (total: number, row: ScraperSourceHealth) =>
          total > 0 ? (
            <Text style={{ fontSize: 12 }}>
              {row.lookup_known} / {total}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: (
          <Tooltip title="Awaria opcjonalnego prefiltra source-index. Detale nie zostały zgubione: scraper wykonał idempotentne upserty, ale warto sprawdzić dostępność Mercury.">
            Prefiltr
          </Tooltip>
        ),
        dataIndex: "source_index_lookups_failed",
        width: 100,
        render: (value: number) =>
          value > 0 ? <Text style={{ color: HEALTH_COLORS.warn }}>{value}</Text> : value,
      },
      {
        title: "CPV",
        dataIndex: "cpv_enriched",
        width: 90,
        render: (value: number, row: ScraperSourceHealth) =>
          row.role === "enrichment" ? <Text strong>{value}</Text> : value > 0 ? value : "—",
      },
      {
        title: "Odrzuty",
        dataIndex: "upserts_failed",
        width: 100,
        render: (value: number) =>
          value > 0 ? <Text style={{ color: HEALTH_COLORS.crit }}>{value}</Text> : value,
      },
      {
        title: (
          <Tooltip title="Oczekiwane 401 z chronionych endpointów. To podzbiór nieudanych żądań, a nie blokada proxy.">
            401 oczek.
          </Tooltip>
        ),
        dataIndex: "requests_auth_required",
        width: 115,
        render: (value: number | undefined) => {
          const authRequired = value ?? 0;
          return authRequired > 0 ? (
            <Text style={{ color: "#2F54EB" }}>{authRequired}</Text>
          ) : (
            authRequired
          );
        },
      },
      {
        title: "RSS max",
        dataIndex: "rss_mb_max",
        width: 110,
        render: (value: number) => (value > 0 ? `${value} MiB` : "—"),
      },
    ],
    [allNew],
  );

  const catalogColumns = useMemo(
    () => [
      {
        title: "Scrapowane źródło",
        dataIndex: "source_name",
        width: 210,
        fixed: "left" as const,
        render: (value: string, row: ScraperCatalogRow) => (
          <Space direction="vertical" size={0}>
            <Text strong>{value}</Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {row.source_key}
            </Text>
          </Space>
        ),
      },
      {
        title: "Scrapowane dane",
        width: 300,
        render: (_: unknown, row: ScraperCatalogRow) => row.catalog?.scrapedData ?? "Brak danych",
      },
      {
        title: "Niescrapowane dane",
        width: 300,
        render: (_: unknown, row: ScraperCatalogRow) =>
          row.catalog?.notScrapedData ?? "Brak danych",
      },
      {
        title: "Dedykowane API",
        width: 190,
        render: (_: unknown, row: ScraperCatalogRow) => (
          <Space direction="vertical" size={2}>
            <YesNo value={row.catalog?.dedicatedApi ?? null} />
            {row.catalog?.noApiReason && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {row.catalog.noApiReason}
              </Text>
            )}
          </Space>
        ),
      },
      {
        title: "Sposób pobierania",
        width: 270,
        render: (_: unknown, row: ScraperCatalogRow) =>
          row.catalog?.acquisition ? (
            <Space direction="vertical" size={2}>
              <Space wrap>
                {row.catalog.acquisition.methods.map((method) => (
                  <Tag key={method}>
                    {{
                      api: "API",
                      "html-http": "HTML przez HTTP",
                      browser: "Przeglądarka",
                      "files-feed": "Pliki / feed",
                    }[method] ?? method}
                  </Tag>
                ))}
              </Space>
              <Text type="secondary">{row.catalog.acquisition.technology}</Text>
            </Space>
          ) : (
            "Brak informacji o metodzie"
          ),
      },
      {
        title: "Aktualność statusu",
        width: 235,
        render: (_: unknown, row: ScraperCatalogRow) => (
          <Space direction="vertical" size={2}>
            <Text>Oceniono: {fmtTime(row.status_evaluated_at)}</Text>
            <Text>Sygnał: {fmtTime(row.last_signal_at)}</Text>
            <Text>Sukces: {fmtTime(row.last_success_at)}</Text>
          </Space>
        ),
      },
      {
        title: "Ostatnie zmiany",
        width: 135,
        render: (_: unknown, row: ScraperCatalogRow) => row.catalog?.lastChangedOn ?? "—",
      },
      {
        title: "Status",
        width: 180,
        render: (_: unknown, row: ScraperCatalogRow) => (
          <Space direction="vertical" size={2}>
            <Tag color={HEALTH_COLORS[row.health]}>{STATUS_LABELS[row.status]}</Tag>
            {row.status_reasons.length > 0 && (
              <Tooltip title={row.status_reasons.join(" • ")}>
                <Text type="secondary" ellipsis style={{ maxWidth: 150, fontSize: 11 }}>
                  {row.status_reasons[0]}
                </Text>
              </Tooltip>
            )}
          </Space>
        ),
      },
      {
        title: "Ostatnia ingerencja / naprawa",
        width: 170,
        render: (_: unknown, row: ScraperCatalogRow) => row.catalog?.lastInterventionOn ?? "—",
      },
      {
        title: "Wyniki",
        width: 210,
        render: (_: unknown, row: ScraperCatalogRow) => (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <Text>Pobierane:</Text>
              <YesNo value={row.catalog?.resultsScraped ?? null} />
            </Space>
            {row.catalog?.resultsScraped === false && (
              <Space size={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Możliwe efektywnie:
                </Text>
                <YesNo value={row.catalog.resultsAcquisitionPossible} />
              </Space>
            )}
          </Space>
        ),
      },
      {
        title: "Ogłoszenia o zmianach",
        width: 250,
        render: (_: unknown, row: ScraperCatalogRow) => (
          <Space direction="vertical" size={2}>
            <Space size={4}>
              <Text>Pobierane:</Text>
              <YesNo value={row.catalog?.changeNoticesScraped ?? null} />
            </Space>
            {row.catalog?.changeNoticesScraped === true && (
              <Space size={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Obsługiwane przez BB:
                </Text>
                <YesNo value={row.catalog.changeNoticesHandledByPlatform} />
              </Space>
            )}
            {row.catalog?.changeNoticesScraped === false && (
              <Space size={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Możliwe efektywnie:
                </Text>
                <YesNo value={row.catalog.changeNoticesAcquisitionPossible} />
              </Space>
            )}
          </Space>
        ),
      },
      {
        title: "Wolumen",
        width: 180,
        render: (_: unknown, row: ScraperCatalogRow) => (
          <Space direction="vertical" size={0}>
            <Text>7 dni: {row.last_7_days ?? "—"}</Text>
            <Text>poprz. miesiąc: {row.previous_month ?? "—"}</Text>
          </Space>
        ),
      },
      {
        title: "% otwartych z poprzedniego miesiąca",
        width: 190,
        render: (_: unknown, row: ScraperCatalogRow) =>
          row.open_share_pct === null
            ? "—"
            : `${row.open_share_pct}% (${row.open_from_previous_month})`,
      },
      {
        title: "Koszt / przetarg (poprz. miesiąc)",
        width: 260,
        render: (_: unknown, row: ScraperCatalogRow) => {
          const cost = row.catalog?.cost;
          if (!cost) return "Brak danych";
          if (cost.kind === "not-tracked") {
            return (
              <Tooltip title={cost.reason}>
                <Tag color={HEALTH_COLORS.warn}>Nieewidencjonowany</Tag>
              </Tooltip>
            );
          }
          return (
            <Space direction="vertical" size={0}>
              <Text>
                {row.cost_per_tender_pln === null
                  ? "—"
                  : `${row.cost_per_tender_pln.toFixed(2)} zł`}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {cost.monthlyPln.toFixed(2)} zł/mies. · {cost.note}
              </Text>
            </Space>
          );
        },
      },
    ],
    [],
  );

  const reconColumns = useMemo(
    () => [
      {
        title: "Źródło (nazwa kanoniczna)",
        dataIndex: "source_name",
        width: 220,
        fixed: "left" as const,
      },
      {
        title: "Telemetria twierdzi",
        dataIndex: "telemetry_created",
        width: 160,
        // Źródło enrichment-only nie tworzy ogłoszeń — jego miarą pracy jest CPV.
        render: (value: number, row: ReconciliationRow) =>
          row.role === "enrichment" ? <Text type="secondary">{row.cpv_enriched} CPV</Text> : value,
      },
      {
        title: "Faktycznie w `auctions`",
        dataIndex: "auctions_created",
        width: 180,
        render: (value: number, row: ReconciliationRow) =>
          row.role === "enrichment" ? <Text type="secondary">—</Text> : value,
      },
      {
        title: "Różnica",
        dataIndex: "diff",
        width: 120,
        render: (value: number, row: ReconciliationRow) =>
          row.role === "enrichment" ? (
            <Text type="secondary">—</Text>
          ) : (
            <Text
              style={{
                color: row.status === "rozjazd" ? HEALTH_COLORS.crit : undefined,
              }}
            >
              {value > 0 ? `+${value}` : value} ({row.drift_pct}%)
            </Text>
          ),
      },
      {
        title: "Status",
        dataIndex: "status",
        width: 140,
        render: (value: ReconciliationRow["status"]) => {
          if (value === "rozjazd") return <Tag color={HEALTH_COLORS.crit}>Rozjazd</Tag>;
          if (value === "brak_danych") return <Tag color={HEALTH_COLORS.unknown}>Brak danych</Tag>;
          if (value === "enrichment")
            return (
              <Tooltip title="Źródło wyłącznie wzbogaca istniejące aukcje (CPV) — zero nowych ogłoszeń to jego normalny stan, nie awaria.">
                <Tag color="#2F54EB">Enrichment</Tag>
              </Tooltip>
            );
          return <Tag color={HEALTH_COLORS.ok}>Zgoda</Tag>;
        },
      },
    ],
    [],
  );

  const columnOrder = [
    "Scrapowane źródło",
    "Status",
    "Sposób pobierania",
    "Aktualność statusu",
    "Dedykowane API",
  ];
  const displayColumns = [...catalogColumns].sort((a, b) => {
    const priority = (title: string) => {
      const index = columnOrder.indexOf(title);
      return index < 0 ? 99 : index;
    };
    return priority(a.title) - priority(b.title);
  });

  return (
    <Card loading={loading && !data}>
      <Title level={4}>Diagnostyka scrapera</Title>
      <Paragraph type="secondary">
        Bieżący status źródeł jest oceniany po dobie roboczej: 24 godziny w dni pon.–pt., z
        pominięciem polskich świąt. Pojedyncze błędy sprawdzisz w szczegółach.
      </Paragraph>
      <Space wrap style={{ marginBottom: 16 }}>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: "Przegląd", value: "overview" },
            { label: "Szczegóły", value: "details" },
          ]}
        />
        <Button
          loading={loading}
          onClick={() =>
            rollingDays ? applyRecent(rollingDays) : void load(applied ?? recentRange(1))
          }
        >
          Odśwież dane
        </Button>
      </Space>
      <Paragraph type="secondary">
        Dane pobrano: {fmtTime(receivedAt)} · Status oceniono:{" "}
        {fmtTime(catalog?.status_evaluated_at)}
        <br />
        Manifest katalogu opublikowano:{" "}
        {fmtTime(catalog?.manifest_published_at ?? rawData?.health.schedule.updated_at)} · Czas:
        Warszawa
      </Paragraph>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Statistic
          title="Łącznie nowych przetargów — wszystkie scrapery"
          value={allNew ?? "—"}
          groupSeparator=" "
        />
        <Text type="secondary">
          {applied ? `${fmtTime(applied.since)} – ${fmtTime(applied.until)}` : "Oczekiwanie na dane"}
          {" · suma raportowana przez scrapery, niezależna od filtrów źródła i statusu"}
        </Text>
      </Card>
      {loading && rawData && (
        <Alert
          type="info"
          message="Trwa odświeżanie. Do zakończenia widoczne są poprzednie dane."
        />
      )}
      {loadError && (
        <Alert
          type="error"
          showIcon
          message="Nie udało się odświeżyć danych"
          description={`${loadError}. Widoczne dane pochodzą z ostatniego udanego odczytu.`}
        />
      )}
      {view === "details" && (
        <>
          <Form layout="inline" style={{ marginBottom: 16 }}>
            <Form.Item
              label="Okno analizy"
              validateStatus={invalidRange ? "error" : undefined}
              help={invalidRange}
            >
              <RangePicker
                showTime
                value={[
                  range.since ? dayjs(range.since).tz("Europe/Warsaw") : null,
                  range.until ? dayjs(range.until).tz("Europe/Warsaw") : null,
                ]}
                onChange={(dates) =>
                  setRange({
                    since: dates?.[0]?.toISOString(),
                    until: dates?.[1]?.toISOString(),
                  })
                }
              />
            </Form.Item>
            <Form.Item>
              <Space wrap>
                <Button
                  type="primary"
                  disabled={!!invalidRange || loading}
                  onClick={() => {
                    setRollingDays(null);
                    void load(range as AnalysisRange);
                  }}
                >
                  Zastosuj
                </Button>
                <Button onClick={() => applyRecent(1)}>Ostatnie 24 h</Button>
                <Button onClick={() => applyRecent(7)}>7 dni</Button>
                <Button onClick={() => applyRecent(30)}>30 dni</Button>
              </Space>
            </Form.Item>
          </Form>
          <Select
            aria-label="Źródło diagnostyki"
            showSearch
            optionFilterProp="label"
            value={source}
            onChange={setSource}
            style={{ minWidth: 300, marginBottom: 12 }}
            options={[
              { label: "Wszystkie źródła", value: "" },
              ...[
                ...new Set([
                  ...(rawData?.catalog?.rows.map((row) => row.source_name) ?? []),
                  ...(rawData?.health.sources.map((row) => row.source_name) ?? []),
                ]),
              ]
                .sort()
                .map((name) => ({ label: name, value: name })),
            ]}
          />
          <Paragraph>
            Analizowany okres: {fmtTime(applied?.since)} — {fmtTime(applied?.until)}.
          </Paragraph>
          <Paragraph type="secondary">
            Wykres trendu obejmuje pełne godziny w tym okresie. Dla cykli zakończonych później nie
            znamy historycznych liczników — nie są doliczane. Bieżący status i wolumeny przeglądu są
            niezależne od tych dat.
          </Paragraph>
        </>
      )}

      {data && health && (
        <>
          {view === "details" && (
            <>
              {telemetrySilent && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 24 }}
                  message="Brak telemetrii — nie wiadomo, czy scraper żyje"
                  description={
                    health.available
                      ? "W wybranym oknie nie ma ANI JEDNEGO runu w kolekcji `scraper_runs`. To znaczy albo że scraper nie odpalał, albo że jeszcze nie zapisuje telemetrii (kolekcja pusta przed jego deployem). Wiersze poniżej są oznaczone jako „brak danych” — nie traktuj ich jako potwierdzenia zdrowia."
                      : "Zapytanie do kolekcji `scraper_runs` nie powiodło się. Poniższe liczby są niekompletne."
                  }
                />
              )}

              {scheduleMissing && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 24 }}
                  message="Brak manifestu harmonogramu — nie wiadomo, jakich źródeł się spodziewać"
                  description={
                    "Kolekcja `scraper_schedule` jest pusta lub niedostępna. Scraper publikuje ją przy " +
                    "starcie poda; do tego czasu widać TYLKO źródła, które faktycznie odpaliły — źródło, " +
                    "które przestało się uruchamiać, nie ma tu wiersza i nie zostanie wykryte. " +
                    "To nie znaczy „zero oczekiwanych źródeł” ani „wszystko OK”."
                  }
                />
              )}

              {recon && !recon.available && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                  message="Rekoncyliacja niedostępna"
                  description={
                    recon.auctions_available
                      ? "Brak telemetrii do zestawienia z kolekcją `auctions`."
                      : "Nie udało się policzyć ogłoszeń w kolekcji `auctions` — niezależna kontrola ingestu jest wyłączona."
                  }
                />
              )}

              {recon && recon.available && recon.drifting_sources > 0 && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 24 }}
                  message={`Rozjazd telemetrii z bazą: ${recon.drifting_sources} źródeł`}
                  description="Liczba ogłoszeń, które scraper raportuje jako utworzone, nie zgadza się z tym, co faktycznie przybyło w kolekcji `auctions`. Telemetria może kłamać (bug w scraperze) — baza nie."
                />
              )}

              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Problemy techniczne w okresie"
                      value={health.summary.crit}
                      valueStyle={{
                        color: health.summary.crit > 0 ? HEALTH_COLORS.crit : undefined,
                      }}
                      suffix={`/ ${health.sources.length}`}
                    />
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Nowe ogłoszenia (okno)"
                      value={telemetrySilent ? "—" : health.totals.created}
                    />
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Odrzuty Mercury"
                      value={telemetrySilent ? "—" : health.totals.upserts_failed}
                      valueStyle={{
                        color: health.totals.upserts_failed > 0 ? HEALTH_COLORS.crit : undefined,
                      }}
                    />
                  </Card>
                </Col>
                <Col xs={12} md={6}>
                  <Card size="small">
                    <Statistic
                      title="Rozjazdy rekoncyliacji"
                      value={recon?.available ? recon.drifting_sources : "—"}
                      valueStyle={{
                        color:
                          recon?.available && recon.drifting_sources > 0
                            ? HEALTH_COLORS.crit
                            : undefined,
                      }}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          )}
          {view === "overview" && (
            <>
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                {(Object.keys(STATUS_LABELS) as (keyof typeof STATUS_LABELS)[]).map((key) => (
                  <Col key={key} xs={12} md={6}>
                    <Card size="small">
                      <Statistic
                        title={STATUS_LABELS[key]}
                        value={
                          catalog?.available
                            ? catalog.rows.filter((row) => row.status === key).length
                            : "—"
                        }
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
              {catalog?.available && catalog.rows.some((row) => row.status !== "working") && (
                <Card size="small" title="Wymagają sprawdzenia" style={{ marginBottom: 16 }}>
                  <Space direction="vertical">
                    {catalog.rows
                      .filter((row) => row.status !== "working")
                      .map((row) => (
                        <Button
                          type="link"
                          key={row.source_name}
                          onClick={() => {
                            setSource(row.source_name);
                            setView("details");
                          }}
                        >
                          {row.source_name}: {STATUS_LABELS[row.status]}
                        </Button>
                      ))}
                  </Space>
                </Card>
              )}
              <Space wrap style={{ marginBottom: 16 }}>
                <Input
                  aria-label="Szukaj źródła"
                  placeholder="Szukaj źródła"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  allowClear
                />
                <Select
                  aria-label="Filtr statusu"
                  value={status}
                  onChange={setStatus}
                  style={{ minWidth: 190 }}
                  options={[
                    { label: "Wszystkie statusy", value: "" },
                    ...Object.entries(STATUS_LABELS).map(([value, label]) => ({
                      value,
                      label,
                    })),
                  ]}
                />
              </Space>
              {catalog?.rows.some((row) => !row.metadata_complete) && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 24 }}
                  message="Niepełny katalog scraperów"
                  description="Co najmniej jeden wpis pochodzi ze starszego manifestu bez wymaganych metadanych. Nowy scraper nie uruchomi się z takim wpisem; po jego wdrożeniu manifest uzupełni się automatycznie."
                />
              )}

              <Card
                size="small"
                title="Tabela scraperów"
                extra={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    bieżący status · wolumeny: ostatnie 7 dni i poprzedni miesiąc
                  </Text>
                }
                style={{ marginBottom: 24 }}
              >
                {!catalog?.available ? (
                  <Alert type="warning" showIcon message="Brak katalogu w manifeście scrapera" />
                ) : (
                  <Table<ScraperCatalogRow>
                    rowKey={(row) => row.source_name}
                    dataSource={catalogRows}
                    pagination={false}
                    size="small"
                    scroll={{ x: 3000 }}
                    columns={displayColumns}
                    expandable={{
                      expandedRowRender: (row) => (
                        <Space direction="vertical">
                          {(row.modes ?? []).map((mode) => (
                            <Text key={mode.source_key}>
                              {mode.source_key}: {STATUS_LABELS[mode.status]} · ostatni sukces:{" "}
                              {fmtTime(mode.last_success_at)} · sygnał:{" "}
                              {fmtTime(mode.last_signal_at)} · obserwacja od:{" "}
                              {fmtTime(mode.observed_since)}
                            </Text>
                          ))}
                          <Button
                            onClick={() => {
                              setSource(row.source_name);
                              setView("details");
                            }}
                          >
                            Otwórz diagnostykę
                          </Button>
                        </Space>
                      ),
                    }}
                  />
                )}
              </Card>
            </>
          )}
          {view === "details" && (
            <>
              <Card
                size="small"
                title="Stan techniczny uruchomień"
                extra={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    okno: {health.window_min} min · rozwiń wiersz, żeby zobaczyć powody
                  </Text>
                }
                style={{ marginBottom: 24 }}
              >
                <Table<ScraperSourceHealth>
                  rowKey={(row) => row.source}
                  dataSource={health.sources}
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                  columns={healthColumns}
                  expandable={{
                    expandedRowRender: (row) => (
                      <Space direction="vertical" size={4} style={{ paddingLeft: 8 }}>
                        {row.reasons.length === 0 ? (
                          <Text type="secondary">Brak zastrzeżeń.</Text>
                        ) : (
                          row.reasons.map((reason) => (
                            <Text key={reason} style={{ fontSize: 12 }}>
                              • {reason}
                            </Text>
                          ))
                        )}
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Heartbeat: {fmtTime(row.last_heartbeat_at)} · koniec:{" "}
                          {fmtTime(row.last_finished_at)} · czas:{" "}
                          {fmtDuration(row.last_duration_ms)} · exit: {row.last_exit_code ?? "—"} ·
                          oczekiwany interwał:{" "}
                          {row.expected_interval_min ? `${row.expected_interval_min} min` : "—"}
                        </Text>
                        {hasActivityHint(row) && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Ostatnia aktywność (migawka najnowszego runu):{" "}
                            {row.last_activity_kind ?? "—"} · start:{" "}
                            {fmtTime(row.last_activity_started_at)} · koniec:{" "}
                            {fmtTime(row.last_activity_done_at)} ·{" "}
                            {activityState(row.last_activity_active_count)}
                          </Text>
                        )}
                        {(row.requests_auth_required ?? 0) > 0 && (
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            Oczekiwane 401: {row.requests_auth_required} — są już wliczone w
                            nieudane żądania, ale nie są blokadą proxy.
                          </Text>
                        )}
                      </Space>
                    ),
                    rowExpandable: () => true,
                  }}
                />
              </Card>

              <Card
                size="small"
                title="Rekoncyliacja — telemetria kontra kolekcja `auctions`"
                extra={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    tolerancja: ±{recon?.tolerance.abs ?? 0} dok. lub{" "}
                    {Math.round((recon?.tolerance.rel ?? 0) * 100)}%
                  </Text>
                }
                style={{ marginBottom: 24 }}
              >
                {recon && recon.rows.length > 0 ? (
                  <Table<ReconciliationRow>
                    rowKey={(row) => row.source_name}
                    dataSource={recon.rows}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                    columns={reconColumns}
                  />
                ) : (
                  <Empty description="Brak danych do zestawienia w tym oknie" />
                )}
              </Card>

              {/* Jakość pól dociąga się WŁASNYM zapytaniem (`/admin/scraper/field-quality`):
              jest kilkanaście razy cięższa od reszty strony, więc doklejona do diagnostyki
              kazałaby odpowiedzi „czy scraper żyje" czekać na „czy dane są dobre".
              Dzięki rozdzieleniu wolna albo padnięta sekcja degraduje jedną kartę, nie stronę. */}
              {applied && (
                <FieldQualitySection range={applied} source={source} refreshKey={receivedAt} />
              )}

              <Card size="small" title="Timeline runów" style={{ marginBottom: 24 }}>
                <RunTimeline data={data} colorBorder={token.colorBorderSecondary} />
              </Card>

              <Card
                size="small"
                title="Nowe ogłoszenia i aktualizacje — trend w wybranym okresie"
                style={{ marginBottom: 24 }}
              >
                <YieldTrend data={data} />
              </Card>

              <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                  <Card size="small" title="Zdrowie ruchu / proxy (okno analizy)">
                    <TrafficHealth sources={health.sources} />
                  </Card>
                </Col>
                <Col xs={24} lg={10}>
                  <Card
                    size="small"
                    title={`Pamięć — szczyt RSS vs limit poda (${data.memory_limit_mb} MiB)`}
                  >
                    <MemoryUsage sources={health.sources} limitMb={data.memory_limit_mb} />
                  </Card>
                </Col>
              </Row>
            </>
          )}
          <Paragraph type="secondary" style={{ marginTop: 24, fontSize: 12 }}>
            Odpowiedź serwera wygenerowano: {fmtTime(data.generated_at)}
          </Paragraph>
        </>
      )}
    </Card>
  );
};
